import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';

// Company logo
const companyLogo = "https://customer-assets.emergentagent.com/job_sleuth-focsani-clone/artifacts/0lecfb0n_Detectiv%20Bacau.jpg";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoHovered, setLogoHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#acasa', label: 'Acasă' },
    { href: '#servicii', label: 'Servicii' },
    { href: '#despre', label: 'Despre Noi' },
    { href: '#blog', label: 'Blog' },
    { href: '#contact', label: 'Contact' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* CSS for logo glow animation */}
      <style>{`
        @keyframes logoGlow {
          0%, 100% {
            box-shadow: 
              0 0 20px rgba(255, 255, 255, 0.4),
              0 0 40px rgba(255, 255, 255, 0.2),
              0 0 60px rgba(255, 255, 255, 0.1);
          }
          50% {
            box-shadow: 
              0 0 30px rgba(255, 255, 255, 0.6),
              0 0 60px rgba(255, 255, 255, 0.4),
              0 0 90px rgba(255, 255, 255, 0.2);
          }
        }
        
        .logo-container {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .logo-container:hover {
          transform: scale(1.08);
          animation: logoGlow 1.5s ease-in-out infinite;
        }
        
        .logo-container:hover .logo-image {
          filter: brightness(1.1) contrast(1.05);
        }
        
        .logo-shine {
          position: absolute;
          inset: -2px;
          background: linear-gradient(
            135deg,
            transparent 0%,
            transparent 40%,
            rgba(255, 255, 255, 0.4) 50%,
            transparent 60%,
            transparent 100%
          );
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        
        .logo-container:hover .logo-shine {
          opacity: 1;
          animation: shineMove 1.5s ease-in-out infinite;
        }
        
        @keyframes shineMove {
          0% {
            transform: translateX(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) rotate(45deg);
          }
        }
      `}</style>
      
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-slate-900/98 backdrop-blur-md shadow-lg border-b border-slate-800'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a 
              href="#acasa" 
              onClick={(e) => scrollToSection(e, '#acasa')} 
              className="flex items-center gap-4"
              onMouseEnter={() => setLogoHovered(true)}
              onMouseLeave={() => setLogoHovered(false)}
              aria-label="Detectivi Bacău - Pagina principală"
            >
              <div 
                className={`logo-container relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full overflow-visible flex-shrink-0
                  ${logoHovered 
                    ? 'shadow-[0_0_40px_rgba(255,255,255,0.6),0_0_60px_rgba(255,255,255,0.4)]' 
                    : 'shadow-[0_0_20px_rgba(255,255,255,0.4)]'
                  }`}
              >
                {/* Outer glow ring */}
                <div className={`absolute inset-[-4px] rounded-full bg-gradient-to-r from-white/30 via-white/50 to-white/30 
                  transition-opacity duration-300 ${logoHovered ? 'opacity-100' : 'opacity-60'}`} 
                />
                
                {/* Main logo container */}
                <div className={`relative w-full h-full rounded-full overflow-hidden 
                  border-[3px] border-white bg-white backdrop-blur-sm
                  transition-all duration-300`}
                >
                  <img 
                    src={companyLogo} 
                    alt="Logo Detectivi Bacău - Agenție de detectivi particulari licențiată IGPR"
                    className={`logo-image w-full h-full object-cover transition-all duration-300
                      ${logoHovered ? 'scale-105 brightness-110' : 'scale-100'}`}
                    loading="eager"
                    fetchpriority="high"
                  />
                  
                  {/* Shine overlay */}
                  <div className="logo-shine" aria-hidden="true" />
                </div>
                
                {/* Pulsing ring on hover */}
                {logoHovered && (
                  <div className="absolute inset-[-8px] rounded-full border-2 border-white/40 animate-ping" aria-hidden="true" />
                )}
              </div>
              
              <div className="hidden sm:block">
                <span className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white 
                  drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] transition-all duration-300
                  ${logoHovered ? 'drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]' : ''}`}
                >
                  {companyInfo.name}
                </span>
                <p className="text-sm text-slate-300 mt-0.5 hidden lg:block font-medium">Detectivi Particulari Licențiați</p>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-10">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-slate-200 hover:text-white transition-colors text-base lg:text-lg font-medium"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-4">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-white text-base lg:text-lg font-semibold bg-amber-500/20 hover:bg-amber-500/30 px-5 py-2.5 rounded-lg transition-colors border border-amber-500/30">
                <Phone className="w-5 h-5" />
                <span>{companyInfo.phone}</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-white p-2"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-slate-900/98 backdrop-blur-md px-4 py-6 space-y-4 border-t border-slate-800">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block text-slate-300 hover:text-white transition-colors font-medium py-2"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 border-t border-slate-800">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-white font-medium">
                <Phone className="w-4 h-4" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
